import {
  Building2,
  FileSignature,
  Funnel,
  Handshake,
  LayoutDashboard,
  Settings,
  User,
  UserCog,
  Users,
  Volleyball,
} from "lucide-react";
import {
  useCanAccess,
  useGetIdentity,
  useTranslate,
  useUserMenu,
} from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import logo from "../root/logos/sliedrecht-sport-sponsoring.svg";
import { getCurrentTab } from "./currentTab";

/**
 * Desktop navigation: a fixed sidebar on the left with the brand lockup, the
 * four main sections, the admin-only section and the user card at the bottom.
 * The mobile layout uses <MobileNavigation> instead.
 */
const Sidebar = () => {
  const { title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  const currentPath = getCurrentTab(location.pathname);

  return (
    <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-sidebar-border bg-white">
      <div className="px-6 pt-6 pb-2">
        <Link to="/" className="block">
          <img className="mx-auto h-auto w-40" src={logo} alt={title} />
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pt-4 pb-4">
        <nav
          aria-label={translate("crm.navigation.label")}
          className="flex flex-col gap-0.5"
        >
          <NavigationItem
            label={translate("ra.page.dashboard")}
            to="/"
            Icon={LayoutDashboard}
            isActive={currentPath === "/"}
          />
          <NavigationItem
            label={translate("resources.sponsorings.views.sponsorings")}
            to="/sponsorings"
            Icon={Handshake}
            isActive={currentPath === "/sponsorings"}
          />
          <NavigationItem
            label={translate("resources.sponsorings.views.ball")}
            to="/balsponsoren"
            Icon={Volleyball}
            isActive={currentPath === "/balsponsoren"}
          />
          <NavigationItem
            label={translate("resources.sponsorings.views.leads")}
            to="/leads"
            Icon={Funnel}
            isActive={currentPath === "/leads"}
          />
          <NavigationItem
            label={translate("resources.companies.name", { smart_count: 2 })}
            to="/companies"
            Icon={Building2}
            isActive={currentPath === "/companies"}
          />
          <NavigationItem
            label={translate("resources.contacts.name", { smart_count: 2 })}
            to="/contacts"
            Icon={Users}
            isActive={currentPath === "/contacts"}
          />
        </nav>
        <AdminNavigation />
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 rounded-xl border border-sidebar-border bg-white py-2 pr-2 pl-1">
          <UserMenu>
            <ProfileMenuItem />
          </UserMenu>
          <UserBlock />
          <div className="ml-auto">
            <RefreshButton />
          </div>
        </div>
      </div>
    </aside>
  );
};

/**
 * Admin-only section. Each entry is guarded by its own permission; when none
 * of them is granted the whole section (heading included) is not rendered.
 */
const AdminNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  const { canAccess: canManageUsers } = useCanAccess({
    resource: "sales",
    action: "create",
  });
  const { canAccess: canManageContractTypes } = useCanAccess({
    resource: "contract_types",
    action: "create",
  });
  const { canAccess: canEditConfiguration } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });

  if (!canManageUsers && !canManageContractTypes && !canEditConfiguration) {
    return null;
  }

  return (
    <div className="mt-7">
      <h2 className="mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground/80 uppercase">
        {translate("crm.navigation.admin")}
      </h2>
      <nav
        aria-label={translate("crm.navigation.admin")}
        className="flex flex-col gap-0.5"
      >
        {canManageUsers ? (
          <NavigationItem
            label={translate("resources.sales.name", { smart_count: 2 })}
            to="/sales"
            Icon={UserCog}
            isActive={!!matchPath("/sales/*", location.pathname)}
          />
        ) : null}
        {canManageContractTypes ? (
          <NavigationItem
            label={translate("resources.contract_types.name", {
              smart_count: 2,
            })}
            to="/contract_types"
            Icon={FileSignature}
            isActive={!!matchPath("/contract_types/*", location.pathname)}
          />
        ) : null}
        {canEditConfiguration ? (
          <NavigationItem
            label={translate("crm.settings.title")}
            to="/settings"
            Icon={Settings}
            isActive={!!matchPath("/settings", location.pathname)}
          />
        ) : null}
      </nav>
    </div>
  );
};

const NavigationItem = ({
  label,
  to,
  Icon,
  isActive,
}: {
  label: string;
  to: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isActive: boolean;
}) => (
  <Link
    to={to}
    aria-current={isActive ? "page" : undefined}
    className={cn(
      "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
      isActive
        ? "bg-brand-blue/10 text-brand-navy"
        : "text-muted-foreground hover:bg-black/[0.04] hover:text-brand-navy",
    )}
  >
    <Icon
      className={cn(
        "size-[18px] shrink-0 transition-colors",
        isActive
          ? "text-brand-blue"
          : "text-muted-foreground group-hover:text-brand-navy",
      )}
      strokeWidth={1.75}
    />
    <span className="truncate">{label}</span>
  </Link>
);

/** Name and role next to the avatar (the <UserMenu> trigger only shows the avatar). */
const UserBlock = () => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "create",
  });
  if (!identity?.fullName) return null;
  return (
    <div className="min-w-0 leading-tight">
      <p className="truncate text-sm font-medium text-brand-navy">
        {identity.fullName}
      </p>
      <p className="truncate text-xs text-muted-foreground">
        {translate(
          isAdmin ? "crm.navigation.role_admin" : "crm.navigation.role_member",
        )}
      </p>
    </div>
  );
};

const ProfileMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Sidebar;
